'use client';

import LocalizedText from '@/common/components/LocalizedText';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionHeader, styles } from '../../product-landing/ProductLanding';
import ClientTabs from '../components/ClientTabs';
import clientStyles from '../components/mcp-clients.module.scss';
import {
  CLAUDE_DIRECTORY_URL,
  CODEX_API_KEY_SNIPPET,
  CODEX_CONFIG_TOML,
  CODEX_LOGIN_COMMAND,
  MCP_SETUP_CLIENTS,
  MCP_SNIPPETS,
  McpSetupClient,
  usesSetupSteps,
} from '../lib/clients';

/**
 * Panels that size the row only while they are open. Codex holds three
 * snippets; overlapping it with the others would leave blank space under the
 * shorter panels, ChatGPT (the default tab) included.
 */
const OWN_HEIGHT_PANELS: ReadonlySet<McpSetupClient> = new Set(['codex']);

export default function SetupSection() {
  const { t } = useTranslation('start');
  const [active, setActive] = useState<McpSetupClient>('chatgpt');
  // A panel can hold several snippets (Codex), so feedback is per snippet id.
  const [copied, setCopied] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const handleCopy = async (id: string, text: string) => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(null), 2000);
    } catch {
      // Keep the snippet selectable without claiming that copying succeeded.
      setCopied(null);
    }
  };

  const renderSnippet = (id: string, text: string) => (
    <>
      <pre className={styles.code}><code>{text}</code></pre>
      <div className={styles.copyAction}>
        <button type="button" onClick={() => void handleCopy(id, text)} className={styles.secondaryButton} aria-live="polite">
          {copied === id ? t('mcp.setup.copiedFeedback') : t('mcp.setup.copyButton')}
        </button>
      </div>
    </>
  );

  const renderPanel = (client: McpSetupClient) => {
    const isSteps = usesSetupSteps(client);
    // Claude is connected from its connector directory; the server URL below
    // the steps is only the manual fallback (custom connector, other clients).
    const isClaude = client === 'claudeDesktop';
    const raw = isSteps ? t(`mcp.setup.tabs.${client}.steps`, { returnObjects: true }) : [];
    const steps = Array.isArray(raw) ? raw.filter((step): step is string => typeof step === 'string') : [];
    return (
      <div className={styles.tabBody}>
        {!isSteps && (
          <p className="mb-4"><code className="text-sm break-all">{t(`mcp.setup.tabs.${client}.path`)}</code></p>
        )}
        <p className="text-sm text-text-secondary leading-relaxed">
          {t(`mcp.setup.tabs.${client}.${isSteps ? 'intro' : 'snippetIntro'}`)}
        </p>
        {isClaude && (
          <div className={clientStyles.directoryAction}>
            <a
              href={CLAUDE_DIRECTORY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.primaryButton}
            >
              {t('mcp.setup.tabs.claudeDesktop.directoryButton')}
              <OpenInNewIcon fontSize="small" aria-hidden="true" />
            </a>
          </div>
        )}
        {isSteps && (
          <>
            <ol className={styles.setupSteps}>
              {steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
            <p className="text-sm text-text-secondary">
              {t(isClaude ? 'mcp.setup.tabs.claudeDesktop.manual' : 'mcp.setup.urlLabel')}
            </p>
          </>
        )}
        {renderSnippet(client, MCP_SNIPPETS[client])}
        {client === 'chatgpt' && <p className={styles.note}>{t('mcp.setup.tabs.chatgpt.note')}</p>}
        {client === 'codex' && (
          // OAuth is the default; the config.toml block and the API key are
          // the alternatives `codex mcp add --help` documents.
          <>
            <p className="text-sm text-text-secondary leading-relaxed mt-4">{t('mcp.setup.tabs.codex.configIntro')}</p>
            {renderSnippet('codex-config', CODEX_CONFIG_TOML)}
            <p className="text-sm text-text-secondary leading-relaxed mt-4">
              {t('mcp.setup.tabs.codex.loginHint')}{' '}
              <code className="text-sm break-all">{CODEX_LOGIN_COMMAND}</code>
            </p>
            <p className="text-sm text-text-secondary leading-relaxed mt-4">{t('mcp.setup.tabs.codex.apiKeyIntro')}</p>
            {renderSnippet('codex-apiKey', CODEX_API_KEY_SNIPPET)}
          </>
        )}
      </div>
    );
  };

  return (
    <section className={styles.section}>
      <div className={styles.split}>
        <div>
          <SectionHeader title={t('mcp.setup.title')} intro={t('mcp.setup.intro')} left />
          <div className={styles.card}>
            <h3>{t('mcp.setup.keyless.title')}</h3>
            <pre className={styles.code}><code>{t('mcp.setup.keyless.url')}</code></pre>
            <p>{t('mcp.setup.keyless.body')}</p>
          </div>
          <p className={styles.note}>
            {t('mcp.setup.apiKeyNote')}{' '}
            <Link href="/rejestracja" className={styles.textLink}><LocalizedText id="text_5db864291b17" /></Link>
          </p>
        </div>
        <div className={`${styles.panel} ${styles.tabsPanel}`}>
          <ClientTabs
            id="mcp-setup"
            label={t('mcp.setup.title')}
            clients={MCP_SETUP_CLIENTS}
            value={active}
            onChange={setActive}
          />
          <div className={clientStyles.setupPanels}>
            {MCP_SETUP_CLIENTS.map((client) => (
              <div
                key={client}
                role="tabpanel"
                id={`mcp-setup-panel-${client}`}
                aria-labelledby={`mcp-setup-tab-${client}`}
                className={OWN_HEIGHT_PANELS.has(client)
                  ? `${clientStyles.setupPanel} ${clientStyles.setupPanelOwnHeight}`
                  : clientStyles.setupPanel}
                hidden={client !== active}
                tabIndex={0}
              >
                {renderPanel(client)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
